/**
 * Utility to update user's last_active timestamp in the users table
 * P2: Central throttle — min 10 min between writes, skip when tab hidden, cross-tab sessionStorage dedup
 */

import { supabase } from '../integrations/supabase/client';

const MIN_WRITE_INTERVAL_MS = 10 * 60 * 1000;
const STALE_FORCE_MS = 15 * 60 * 1000;
const SESSION_STORAGE_KEY = 'blissnet:last_active_written';

let lastWrittenAtMemory = 0;

function readLastWrittenAt(): number {
  try {
    if (typeof sessionStorage !== 'undefined') {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        const ts = parseInt(stored, 10);
        if (!Number.isNaN(ts)) {
          lastWrittenAtMemory = Math.max(lastWrittenAtMemory, ts);
          return ts;
        }
      }
    }
  } catch {
    // private mode / blocked storage — fall back to in-memory
  }
  return lastWrittenAtMemory;
}

function persistLastWrittenAt(ts: number): void {
  lastWrittenAtMemory = ts;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SESSION_STORAGE_KEY, String(ts));
    }
  } catch {
    // ignore
  }
}

export type UpdateUserLastActiveOptions = {
  force?: boolean;
  foregroundResume?: boolean;
};

function shouldSkipWrite(opts?: UpdateUserLastActiveOptions): boolean {
  if (opts?.force) return false;

  if (typeof document !== 'undefined' && document.hidden) {
    return true;
  }

  const lastWritten = readLastWrittenAt();
  if (lastWritten === 0) return false;

  const age = Date.now() - lastWritten;
  if (opts?.foregroundResume && age >= STALE_FORCE_MS) {
    return false;
  }

  return age < MIN_WRITE_INTERVAL_MS;
}

/**
 * Update the last_active timestamp for a user in the users table
 * @param userId - The user ID to update
 * @param opts.force - Bypass throttle (user-initiated actions)
 * @returns Promise<boolean> - true if successful, false otherwise or if skipped
 */
export async function updateUserLastActive(
  userId: string,
  opts?: UpdateUserLastActiveOptions,
): Promise<boolean> {
  if (!userId) return false;

  if (shouldSkipWrite(opts)) {
    return false;
  }

  try {
    const { error } = await supabase
      .from('users')
      .update({
        last_active: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (error) {
      console.error('❌ Error updating last_active:', error);
      return false;
    }

    persistLastWrittenAt(Date.now());
    return true;
  } catch (error) {
    console.error('❌ Exception updating last_active:', error);
    return false;
  }
}

/**
 * Write last_active when the tab returns to foreground (respects throttle; stale >15 min always writes)
 */
export async function updateUserLastActiveOnForeground(userId: string): Promise<boolean> {
  if (!userId) return false;
  return updateUserLastActive(userId, { foregroundResume: true });
}

/**
 * Update last_active for the current authenticated user immediately (bypasses throttle)
 */
export async function updateLastActiveNow(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.id) {
      return updateUserLastActive(user.id, { force: true });
    }
    return false;
  } catch (error) {
    console.error('❌ Exception in updateLastActiveNow:', error);
    return false;
  }
}
